import { useLocalSearchParams } from 'expo-router';
import { useCallback, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FlatList, type ListRenderItem } from 'react-native';
import { v4 } from 'uuid';
import { calculateElapsedMs } from '@/components/Event/EventClock/EventClock.utils';
import EventScorekeepingPanelBadgeItem, {
  EventScorekeepingPanelBadgeItemFragment,
} from '@/components/Event/EventScorekeeping/EventScorekeepingPanel/EventScorekeepingPanelBadge/EventScorekeepingPanelBadgeItem';
import Box from '@/components/UI/Box';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import type { EventScorekeepingPanelFormValues } from '../../EventScorekeeping.types';

type BadgeListItem = {
  id: string;
} & FragmentOf<typeof EventScorekeepingPanelBadgeItemFragment>;

const EMPTY_BADGES: BadgeListItem[] = [];

export const EventScorekeepingPanelBadgeFragment = graphql(/* GraphQL */ `
  fragment EventScorekeepingPanelBadge on event {
    id
    event_segment_group {
      id
      direction
    }
    event_clock {
      event_id
      start_datetime
      elapsed_segment_ms
      is_running
      event_segment {
        id
        title
        duration
        duration_offset
        is_duration_capped
      }
    }
    root_owner {
      id
      badges_by_tag_group(
        order_by: { title: asc }
        where: { is_stat: { _eq: true } }
      ) {
        id
        ...EventScorekeepingPanelBadgeItem
      }
    }
  }
`, [EventScorekeepingPanelBadgeItemFragment]);

function EventScorekeepingPanelBadgeListItem({
  item,
  onPressBadge,
  selectedBadgeId,
}: {
  item: BadgeListItem;
  onPressBadge: (badgeFragment: BadgeListItem) => void;
  selectedBadgeId?: string | null;
}) {
  const onPress = () => {
    onPressBadge(item);
  };

  return (
    <EventScorekeepingPanelBadgeItem
      fragment={item}
      isChecked={item.id === selectedBadgeId}
      onPress={onPress}
    />
  );
}

function EventScorekeepingPanelBadge({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelBadgeFragment>
    | null
    | undefined;
}) {
  const { badgeAssigneeId } = useLocalSearchParams();
  const event = readFragment(EventScorekeepingPanelBadgeFragment, fragment);
  const rootEvent = event?.root_owner?.[0];
  const ref = useRef<FlatList>(null);
  const { getValues, setValue, control } =
    useFormContext<EventScorekeepingPanelFormValues>();
  const selectedBadge = useWatch({
    control,
    name: 'selectedBadge',
  });
  const eventClock = event?.event_clock;
  const eventSegment = eventClock?.event_segment;
  const selectedBadgeId = selectedBadge?.id ?? null;
  const onPressBadge = useCallback(
    (badgeFragment: BadgeListItem) => {
      setValue('isEditing', false);
      const badge = readFragment(
        EventScorekeepingPanelBadgeItemFragment,
        badgeFragment,
      );
      const badgeAttributes = badge.badge_attributes;
      const selectedBadge = getValues('selectedBadge');
      const selectedTeam = getValues('selectedTeam');
      if (badge.id === selectedBadge?.id) {
        setValue('selectedBadge', null);
        return;
      }
      const selectedPerson = getValues('selectedPerson');
      const recipients = getValues('recipients');
      if (
        !recipients.length &&
        !badgeAssigneeId &&
        !selectedPerson &&
        !selectedBadge
      ) {
        const calculatedElapsedMs = calculateElapsedMs({
          isRunning: eventClock?.is_running ?? false,
          startDatetime: eventClock?.start_datetime ?? null,
          elapsedSegmentMs: eventClock?.elapsed_segment_ms ?? 0,
          isDurationCapped: eventSegment?.is_duration_capped ?? false,
          duration: eventSegment?.duration ?? 0,
        });
        if (eventSegment) {
          setValue('selectedTime', {
            elapsed_segment_ms: calculatedElapsedMs,
            event_segment: eventSegment,
          });
        }
      }
      if (selectedPerson?.id && selectedTeam?.id) {
        const newRecipient = {
          id: v4(),
          badge,
          badge_assignee_attributes: badgeAttributes.map((attribute) => {
            return {
              id: v4(),
              badge_attribute: attribute,
              value: attribute.default_value,
            };
          }),
          person_team: {
            person_id: selectedPerson.id,
            team_id: selectedTeam.id,
            jersey_number: selectedPerson?.jersey_number ?? null,
            person: selectedPerson,
            team: selectedTeam,
          },
          team: selectedTeam,
          isDeleted: false,
          isNew: true,
        };
        setValue('selectedPerson', null);
        setValue('recipients', [...recipients, newRecipient], {
          shouldDirty: true,
        });
        if (badge.can_quick_assign) {
          setValue('selectedRecipientId', newRecipient.id, {
            shouldDirty: true,
          });
        }
        return;
      }
      if (badge.id) {
        setValue('selectedBadge', badge);
      }
    },
    [badgeAssigneeId, eventClock, eventSegment, getValues, setValue],
  );
  const renderBadgeItem = useCallback<ListRenderItem<BadgeListItem>>(
    ({ item }) => {
      return (
        <EventScorekeepingPanelBadgeListItem
          item={item}
          selectedBadgeId={selectedBadgeId}
          onPressBadge={onPressBadge}
        />
      );
    },
    [onPressBadge, selectedBadgeId],
  );
  const badges = rootEvent?.badges_by_tag_group ?? EMPTY_BADGES;

  return (
    <Box flex direction="horizontal" space="md" py="md">
      <FlatList
        showsHorizontalScrollIndicator={false}
        ref={ref}
        style={{
          width: '100%',
        }}
        contentContainerStyle={{
          paddingHorizontal: 4,
        }}
        nestedScrollEnabled
        keyExtractor={(item) => item.id}
        horizontal
        data={badges}
        extraData={selectedBadgeId}
        renderItem={renderBadgeItem}
      />
    </Box>
  );
}
export default EventScorekeepingPanelBadge;
